import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { BaseButton } from "../../styles/ButtonStyles";
import { authorizedHttpGet, authorizedHttpPost } from '../../api/httpCalls';
import urls from "../../api/urls.json";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 10px;
  width: 80%;
  max-width: 800px;
  max-height: 80%;
  overflow-y: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
`;

const Td = styled.td`
  border: 1px solid #ddd;
  padding: 8px;
`;

const HelperText = styled.p`
  margin: 8px 0 16px;
  font-size: 14px;
  color: #333;
`;

const SubscriptionsModal = ({ closeModal, userRef }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editDate, setEditDate] = useState(new Date());
  const [defaultEndDate, setDefaultEndDate] = useState('');

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const formatDateForApi = (date) => {
    if (!date) {
      return '';
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateForDisplay = (value) => {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '-';
    }
    return date.toLocaleDateString();
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    const response = await authorizedHttpGet(`${urls.LIST_SUBSCRIPTIONS}?ref=${userRef}`);
    if (response.status) {
      setSubscriptions(response.data.subscriptions || []);
    }
    setLoading(false);
  };

  const disableSubscription = async (id) => {
    const response = await authorizedHttpPost(urls.ENABLE_SUBSCRIPTION, { id });
    if (response.status) {
      fetchSubscriptions();
    }
  };

  const openEnableDatePicker = (sub) => {
    setEditingId(sub.id);
    const parsedDate = sub.subscription_end_time ? new Date(sub.subscription_end_time) : new Date();
    setEditDate(Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate);
    setDefaultEndDate(sub.subscription_end_date_display || formatDateForDisplay(sub.subscription_end_time));
  };

  const enableSubscriptionWithDate = async (id) => {
    const response = await authorizedHttpPost(urls.ENABLE_SUBSCRIPTION, { id, end_date: formatDateForApi(editDate) });
    if (response.status) {
      setEditingId(null);
      setDefaultEndDate('');
      fetchSubscriptions();
      return;
    }

    if (response.data?.default_end_date) {
      setDefaultEndDate(response.data.default_end_date);
    }
  };

  const closeEditor = () => {
    setEditingId(null);
    setDefaultEndDate('');
  };

  return (
    <ModalOverlay>
      <ModalContent>
        <h2>Manage Subscriptions for {userRef}</h2>
        {editingId ? <HelperText>Default end date: {defaultEndDate || '-'}</HelperText> : null}
        {loading ? (
          <p>Loading...</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Alias Name</Th>
                <Th>Active</Th>
                <Th>End Date</Th>
                <Th>Start Date</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((sub) => (
                <tr key={sub.id}>
                  <Td>{sub.alias_name || sub.plan_type}</Td>
                  <Td>{sub.is_active ? 'Yes' : 'No'}</Td>
                  <Td>
                    {editingId === sub.id ? (
                      <DatePicker
                        selected={editDate}
                        onChange={(date) => setEditDate(date)}
                        dateFormat="yyyy-MM-dd"
                      />
                    ) : (
                      sub.subscription_end_date_display || formatDateForDisplay(sub.subscription_end_time)
                    )}
                  </Td>
                  <Td>{formatDateForDisplay(sub.created_at)}</Td>
                  <Td>
                    {sub.is_active ? (
                      <BaseButton onClick={() => disableSubscription(sub.id)}>
                        Disable
                      </BaseButton>
                    ) : editingId === sub.id ? (
                      <>
                        <BaseButton onClick={() => enableSubscriptionWithDate(sub.id)}>Enable</BaseButton>
                        <BaseButton onClick={closeEditor}>Cancel</BaseButton>
                      </>
                    ) : (
                      <BaseButton onClick={() => openEnableDatePicker(sub)}>
                        Enable
                      </BaseButton>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        <BaseButton onClick={closeModal}>Close</BaseButton>
      </ModalContent>
    </ModalOverlay>
  );
};

export default SubscriptionsModal;
